import { useEffect, useState } from "react";
import { toast } from "sonner";
import { errText } from "@/lib/api";
import { Panel, Btn, useOps } from "./ui";
import { usePreview } from "./usePreview";

const FIELDS = [
  ["match_label", "Match label (on the targeting frame)"],
  ["replica_label", "Digital replica label"],
  ["hex_word", "Bright hex row (max 10 characters)"],
  ["data_lines", "Data block (one line per row; the first line is the big one)", true],
  ["pill_text", "Footer line (small, bottom-left)"],
  ["message_1", "M's message — line 1 (1–2 letters get scrambled)"],
  ["message_2", "M's message — link line (always fully legible)"],
];

function saveBlob(blob, name) {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 5000);
}

function Preview({ api, tid, layout, copy, label }) {
  const p = usePreview(api, tid, layout, copy);
  const [dl, setDl] = useState(false);
  const download = async () => {  // full-size render of exactly what's previewed (test face, unsaved edits)
    setDl(true);
    try {
      const r = await api.post(`/artifacts/${tid}/preview`, { ...layout, copy }, { params: { full: true }, responseType: "blob" });
      saveBlob(r.data, `${tid}-preview.jpg`);
    } catch (e) { toast.error("Download failed"); } finally { setDl(false); }
  };
  return (
    <div className="space-y-1" data-testid={`signal-preview-${tid}`}>
      <div className="flex items-center gap-2 text-[11px] text-slate-400">{label}{p.busy && <span className="text-slate-500">rendering…</span>}</div>
      {p.url ? <img data-testid={`signal-preview-img-${tid}`} src={p.url} alt={label} className="max-h-[70vh] w-auto rounded border border-[#243144]" />
        : <div className="text-xs text-slate-500">Rendering…</div>}
      {p.url && <Btn variant="ghost" onClick={download} disabled={dl} data-testid={`signal-download-${tid}`}>{dl ? "Preparing…" : "Download full size"}</Btn>}
    </div>
  );
}

function Wording({ d, copy, setCopy, saved, onSaved }) {
  const { api } = useOps();
  const dirty = JSON.stringify(copy) !== JSON.stringify(saved);
  const save = async () => {
    try { const r = await api.put(`/artifact-copy/${d.family}`, copy); onSaved(r.data.copy); toast.success("Wording saved — new and re-downloaded posters use it"); }
    catch (e) { toast.error(errText(e)); }
  };
  return (
    <Panel title="Wording (both sizes)" testid="signal-wording" actions={<>
      {dirty && <span className="text-[10px] text-amber-300">unsaved</span>}
      <Btn variant="ghost" onClick={() => setCopy(d.defaults)} data-testid="signal-wording-defaults">Defaults</Btn>
      <Btn onClick={save} disabled={!dirty} data-testid="signal-wording-save">Save wording</Btn>
    </>}>
      <div className="grid gap-2 md:grid-cols-2">
        {FIELDS.map(([k, label, multi]) => (
          <label key={k} className={`block text-[11px] text-slate-400 ${multi ? "md:row-span-2" : ""}`}>{label}
            {multi ? <textarea rows={5} value={copy[k] || ""} onChange={(e) => setCopy({ ...copy, [k]: e.target.value })} data-testid={`signal-copy-${k}`}
              className="mono mt-1 w-full rounded border border-[#243144] bg-[#0f1520] p-1.5 text-xs text-slate-200" />
              : <input value={copy[k] || ""} maxLength={k === "hex_word" ? 10 : 400} onChange={(e) => setCopy({ ...copy, [k]: e.target.value })} data-testid={`signal-copy-${k}`}
                className="mono mt-1 w-full rounded border border-[#243144] bg-[#0f1520] p-1.5 text-xs text-slate-200" />}
          </label>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-slate-500">Variables: <span className="mono text-sky-300">{d.vars}</span> — {"{days}"} counts down to 01/02/2030 and is recalculated whenever a poster is downloaded.</div>
      <div className="mt-3 flex flex-wrap items-center gap-4 border-t border-[#243144] pt-3 text-[11px] text-slate-400">
        <label className="flex items-center gap-2">Corruption strength (stills + Story video)
          <select value={copy.corruption || "normal"} onChange={(e) => setCopy({ ...copy, corruption: e.target.value })} data-testid="signal-corruption"
            className="rounded border border-[#243144] bg-[#0f1520] px-1.5 py-1 text-xs text-slate-200">
            <option value="calm">Calm</option><option value="normal">Normal</option><option value="violent">Violent</option>
          </select>
        </label>
        <label className="flex items-center gap-2">Footer line
          <select value={copy.footer_style || "clear"} onChange={(e) => setCopy({ ...copy, footer_style: e.target.value })} data-testid="signal-footer-style"
            className="rounded border border-[#243144] bg-[#0f1520] px-1.5 py-1 text-xs text-slate-200">
            <option value="clear">Clear</option><option value="subtle">Subtle</option>
          </select>
        </label>
        <label className="flex items-center gap-2">Story video sound
          <select value={copy.video_static || "generated"} onChange={(e) => setCopy({ ...copy, video_static: e.target.value })} data-testid="signal-video-static"
            className="rounded border border-[#243144] bg-[#0f1520] px-1.5 py-1 text-xs text-slate-200">
            <option value="generated">Generated glitch</option><option value="upload">Uploaded static loop</option><option value="off">Off</option>
          </select>
        </label>
        {copy.video_static === "upload" && !d.static_uploaded && (
          <span className="text-amber-300" data-testid="signal-static-missing">No static loop uploaded yet (Content slots → Radio) — the video stays silent until it is.</span>
        )}
      </div>
    </Panel>
  );
}

function VideoPreview({ api, layout, copy }) {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post("/artifacts/dossier_story/preview-video", { ...(layout || {}), copy }, { responseType: "blob", timeout: 120000 });
      setUrl(URL.createObjectURL(r.data));
    } catch (e) { toast.error("Video preview failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1" data-testid="signal-preview-video">
      <div className="flex items-center gap-2 text-[11px] text-slate-400">Story video — 6 s loop (test face)
        <Btn variant="ghost" onClick={go} disabled={busy} data-testid="signal-render-video">{busy ? "Rendering…" : url ? "Re-render" : "Render preview video"}</Btn>
      </div>
      {url ? <>
        <video data-testid="signal-preview-video-player" src={url} autoPlay loop muted playsInline controls className="max-h-[70vh] w-auto rounded border border-[#243144]" />
        <a href={url} download="dossier_story-preview.mp4" data-testid="signal-download-video"
          className="inline-block rounded px-2 py-1 text-[11px] text-sky-300 hover:bg-[#1a2432]">Download MP4</a>
      </>
        : <div className="flex h-40 w-[240px] items-center justify-center rounded border border-dashed border-[#243144] text-[11px] text-slate-500">{busy ? "Rendering (about 5–15 s)…" : "Not rendered yet"}</div>}
    </div>
  );
}

export default function SignalPanels({ tpl, layout }) {
  const { api } = useOps();
  const [d, setD] = useState(null);
  const [copy, setCopy] = useState(null);
  const [pair, setPair] = useState(null);
  useEffect(() => {
    api.get(`/artifact-copy/${tpl.family}`).then((r) => { setD(r.data); setCopy(r.data.copy); });
    if (tpl.pair) api.get(`/artifacts/${tpl.pair}`).then((r) => setPair(r.data));
  }, [api, tpl.family, tpl.pair]);
  if (!d || !copy) return null;
  const sizes = [{ tid: tpl.id, layout, label: `${tpl.label} — ${tpl.width}×${tpl.height} (editing)` }];
  if (pair) sizes.push({ tid: pair.id, layout: pair.layout, label: `${pair.label} — ${pair.width}×${pair.height}` });
  if (tpl.height > (pair?.height || 0)) sizes.reverse();
  return (
    <div className="space-y-3 2xl:col-span-2">
      <Panel title="Live preview — test face, both sizes" testid="signal-previews">
        <div className="flex flex-wrap items-start gap-4">
          {sizes.map((s) => <Preview key={s.tid} api={api} tid={s.tid} layout={s.layout} copy={copy} label={s.label} />)}
          <VideoPreview api={api} layout={tpl.id === "dossier_story" ? layout : pair?.layout} copy={copy} />
        </div>
      </Panel>
      <Wording d={{ ...d, family: tpl.family }} copy={copy} setCopy={setCopy} saved={d.copy} onSaved={(c) => setD({ ...d, copy: c })} />
    </div>
  );
}
